import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
    website: "", // honeypot
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🛡️ Honeypot spam protection
    if (formData.website) return;

    try {
      setLoading(true);

      // 1️⃣ Save to Firestore
      await addDoc(collection(db, "contacts"), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: formData.service,
        message: formData.message,
        createdAt: serverTimestamp(),
      });

      // 2️⃣ Email to Admin
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      // 3️⃣ Auto-reply to User
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID,
        {
          to_name: formData.name,
          to_email: formData.email,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      toast.success("Thank you! We will contact you soon.");

      // Reset
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        message: "",
        website: "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Skyreti EduCore</title>
        <meta
          name="description"
          content="Contact Skyreti EduCore for website development, app development, design, and digital marketing services."
        />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <section className="pt-28 pb-16 px-4 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-black mb-4">
            Let’s Work Together
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to transform your vision? Get in touch with us today.
          </p>
        </section>

        {/* Contact Form */}
        <section className="pb-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Card className="bg-card-lime border-none">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="hidden"
                  />

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Your Name
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Contact Number
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      pattern="[0-9]{10}"
                      className="bg-white"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Company / Organization
                      </label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Service Interested In
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-black"
                      >
                        <option value="">Select a service</option>
                        <option value="website">Website Development</option>
                        <option value="app">App Development</option>
                        <option value="design">Design Services</option>
                        <option value="marketing">Digital Marketing</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Project Details
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="bg-white"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-black text-white hover:bg-gray-800 py-6 text-lg"
                  >
                    {loading ? "Sending..." : (
                      <>
                        <Send className="mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="grid md:grid-cols-3 gap-8 mt-12 text-center">
              <Info icon={<Mail />} label="Email" value="contact.skyreti@gmail.com" />
              <Info icon={<Phone />} label="Phone" value="+91 7492973201" />
              <Info icon={<MapPin />} label="Location" value="Rajkot, Gujarat" />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const Info = ({ icon, label, value }) => (
  <div>
    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-3 text-white">
      {icon}
    </div>
    <div className="text-sm text-gray-600">{label}</div>
    <div className="font-medium text-black">{value}</div>
  </div>
);

export default Contact;
